import React from 'react';
import styled, { keyframes, css } from 'styled-components';

const SoftwarePricing = () => {
  const pricingPlans = [
    {
      id: 'consultation',
      name: 'AI Consultation',
      description: 'Strategic consultation on implementing AI in your business, with actionable recommendations.',
      price: 199,
      features: [
        '60-Minute Expert Consultation',
        'AI Integration Assessment',
        'Personalized Strategy Document',
        'Implementation Roadmap',
        'ROI Projection Analysis',
        'Follow-up Email Support'
      ],
      isPopular: false,
      buttonText: 'Book Your Consultation'
    },
    {
      id: 'custom-development',
      name: 'Custom AI Integration',
      description: 'Custom AI integration into your existing systems or development of AI-powered web solutions.',
      price: 1000,
      features: [
        'Full AI Needs Assessment',
        'Custom Solution Development',
        'Integration of Latest AI Models',
        'API & System Development',
        '90-Day Support Included',
        'Training & Documentation',
        '100% Money-Back Guarantee'
      ],
      isPopular: true,
      buttonText: 'Start Your Project'
    },
    {
      id: 'enterprise',
      name: 'Full-Scale AI Solution',
      description: 'Comprehensive AI-powered applications built from the ground up by senior engineers.',
      price: 8500,
      features: [
        'Complete Custom Development',
        'Native Mobile Applications',
        'Web Applications & Dashboards',
        'AI Model Fine-Tuning',
        'Unlimited Revisions',
        '1-Year Premium Support',
        'Source Code Ownership',
        'Dedicated Project Manager'
      ],
      isPopular: false,
      buttonText: 'Request a Quote'
    }
  ];

  return (
    <PricingContainer id="pricing">
      <SectionHeader>
        <PricingBadge>OUR SOLUTIONS</PricingBadge>
        <SectionTitle>AI Services For <HighlightText>EVERY BUSINESS NEED</HighlightText></SectionTitle>
        <SectionDescription>
          From initial consultation to complete custom solutions. <StrongText>All services come with our 100% satisfaction guarantee!</StrongText>
        </SectionDescription>
      </SectionHeader>
      
      <PricingGrid>
        {pricingPlans.map((plan) => (
          <PricingCard key={plan.id} isPopular={plan.isPopular}>
            {plan.isPopular && <PopularBadge>MOST POPULAR</PopularBadge>}
            <PlanName>{plan.name}</PlanName>
            <PlanDescription>{plan.description}</PlanDescription>
            
            <PriceContainer>
              <Currency>$</Currency>
              <Price>{plan.price}</Price>
              {plan.id === 'custom-development' && <PlanNote>starting price</PlanNote>}
              {plan.id === 'enterprise' && <PlanNote>starting price</PlanNote>}
            </PriceContainer>
            
            <SavingsBadge>
              {plan.id === 'consultation' 
                ? '61% OFF STANDARD RATE' 
                : plan.id === 'custom-development'
                  ? 'PRICE VARIES BY PROJECT SCOPE'
                  : 'US-BASED SENIOR DEVELOPERS'}
            </SavingsBadge>
            
            <FeaturesList>
              {plan.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureCheckmark>✓</FeatureCheckmark>
                  {feature}
                </FeatureItem>
              ))}
            </FeaturesList>
            
            <PlanCTA isPopular={plan.isPopular} href="#contact">
              {plan.buttonText}
            </PlanCTA>
          </PricingCard>
        ))}
      </PricingGrid>
      
      <GuaranteeSection>
        <GuaranteeIcon>🛡️</GuaranteeIcon>
        <GuaranteeText>
          <GuaranteeTitle>100% Money-Back Guarantee</GuaranteeTitle>
          <GuaranteeDescription>
            If you're not completely satisfied with our services, we'll refund your investment in full. We only succeed when you do.
          </GuaranteeDescription>
        </GuaranteeText>
      </GuaranteeSection>
      
      <EnterpriseBox>
        <EnterpriseContent>
          <EnterpriseTitle>Need a Larger Custom AI Project?</EnterpriseTitle>
          <EnterpriseDescription>
            For enterprise-level AI solutions, large-scale deployments, or projects with budgets over $25,000, we offer specialized consulting and development teams with extensive experience.
          </EnterpriseDescription>
        </EnterpriseContent>
        <EnterpriseButton href="#contact">Schedule a Call</EnterpriseButton>
      </EnterpriseBox>
    </PricingContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseHighlight = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(255, 71, 87, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
  }
`;

const PricingContainer = styled.section`
  padding: 100px 80px;
  background-color: #f8f9fa;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const PricingBadge = styled.div`
  display: inline-block;
  background-color: #FF4757;
  color: white;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  animation: ${pulseHighlight} 2s infinite;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HighlightText = styled.span`
  color: #FF4757;
  display: block;
  margin-top: 10px;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 40px;
`;

const StrongText = styled.span`
  font-weight: 700;
  color: #024A5F;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  position: relative;
  background-color: white;
  border-radius: 12px;
  padding: 40px 30px;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.8s ease-out;
  border: 1px solid ${({ isPopular }) => isPopular ? '#FF4757' : '#eaeaea'};
  box-shadow: ${({ isPopular }) => isPopular 
    ? '0 20px 40px rgba(255, 71, 87, 0.15)' 
    : '0 10px 30px rgba(0, 0, 0, 0.05)'};
  transform: ${({ isPopular }) => isPopular ? 'scale(1.05)' : 'scale(1)'};
  z-index: ${({ isPopular }) => isPopular ? '2' : '1'};
  
  &:hover {
    transform: ${({ isPopular }) => isPopular ? 'scale(1.08)' : 'translateY(-10px)'};
    box-shadow: ${({ isPopular }) => isPopular 
      ? '0 25px 50px rgba(255, 71, 87, 0.2)' 
      : '0 15px 35px rgba(0, 0, 0, 0.1)'};
  }
  
  @media (max-width: 991px) {
    transform: scale(1);
    
    &:hover {
      transform: translateY(-10px);
    }
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: #FF4757;
  color: white;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 8px 20px;
  border-radius: 30px;
  letter-spacing: 0.5px;
  box-shadow: 0 5px 15px rgba(255, 71, 87, 0.3);
`;

const PlanName = styled.h3`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 10px;
  font-weight: 700;
`;

const PlanDescription = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 25px;
  min-height: 50px;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-bottom: 15px;
`;

const Currency = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-right: 5px;
`;

const Price = styled.span`
  font-size: 3.5rem;
  font-weight: 800;
  color: #024A5F;
  line-height: 1;
`;

const SavingsBadge = styled.div`
  background-color: #e9f7fe;
  color: #024A5F;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 20px;
  width: fit-content;
  margin: 0 auto 25px;
`;

const FeaturesList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-bottom: 30px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 0.95rem;
  color: #666;
`;

const FeatureCheckmark = styled.span`
  color: #00b894;
  font-weight: bold;
  margin-right: 10px;
`;

const PlanCTA = styled.a`
  display: block;
  width: 100%;
  padding: 16px;
  text-align: center;
  border-radius: 8px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  
  ${({ isPopular }) => isPopular 
    ? css`
      background: linear-gradient(90deg, #FF4757, #FF6B81);
      color: white;
      box-shadow: 0 8px 15px rgba(255, 71, 87, 0.3);
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 25px rgba(255, 71, 87, 0.4);
      }
    `
    : css`
      background: white;
      color: #024A5F;
      border: 2px solid #024A5F;
      
      &:hover {
        background: #024A5F;
        color: white;
      }
    `
  }
`;

const GuaranteeSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  background: white;
  border-radius: 12px;
  padding: 30px 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 60px;
  border: 1px solid #eaeaea;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 30px 20px;
  }
`;

const GuaranteeIcon = styled.div`
  font-size: 3rem;
`;

const GuaranteeText = styled.div`
  flex: 1;
`;

const GuaranteeTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const GuaranteeDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
`;

const EnterpriseBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #024A5F, #0871A4);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(2, 74, 95, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 20px;
    padding: 30px 20px;
  }
`;

const EnterpriseContent = styled.div`
  flex: 1;
`;

const EnterpriseTitle = styled.h4`
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
  margin-bottom: 15px;
`;

const EnterpriseDescription = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  max-width: 600px;
`;

const EnterpriseButton = styled.a`
  display: inline-block;
  background: white;
  color: #024A5F;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 16px 30px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  margin-left: 30px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const PlanNote = styled.span`
  font-size: 0.9rem;
  color: #666;
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export default SoftwarePricing; 